<template>
  <input
    ref="inputRef"
    v-model="displayValue"
    type="text"
    :placeholder="placeholder"
    :disabled="disabled"
    class="form-control"
    @input="handleInput"
    @keydown="handleKeydown"
    @paste="handlePaste"
    @blur="handleBlur"
    @focus="handleFocus"
  />
</template>

<script>
export default {
  name: 'DateInput',
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: 'MM/DD/YYYY'
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      displayValue: '',
      isFocused: false
    };
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newValue) {
        if (!this.isFocused) {
          this.displayValue = this.formatDate(newValue || '');
        }
      }
    }
  },
  methods: {
    handleInput(event) {
      const inputEl = event.target;
      const cursorPos = inputEl.selectionStart;
      const rawValue = inputEl.value;

      // Count digits before cursor in the raw input
      let digitsBefore = 0;
      for (let i = 0; i < cursorPos; i++) {
        if (/\d/.test(rawValue[i])) {
          digitsBefore++;
        }
      }

      const cleaned = this.cleanDate(rawValue);
      const formatted = this.formatDate(cleaned);

      this.displayValue = formatted;

      // Find position in formatted string after the same number of digits
      let newCursorPos = formatted.length;
      if (digitsBefore === 0) {
        newCursorPos = 0;
      } else {
        let digitsFound = 0;
        for (let i = 0; i < formatted.length; i++) {
          if (/\d/.test(formatted[i])) {
            digitsFound++;
            if (digitsFound === digitsBefore) {
              newCursorPos = i + 1;
              break;
            }
          }
        }
      }

      this.$nextTick(() => {
        inputEl.setSelectionRange(newCursorPos, newCursorPos);
      });

      // Only emit valid dates in YYYY-MM-DD format
      const isoDate = this.toISODate(cleaned);
      if (isoDate || cleaned === '') {
        this.$emit('update:modelValue', isoDate);
      }
    },
    handleKeydown(event) {
      // Allow: backspace, delete, tab, escape, enter
      if ([8, 9, 13, 27, 46].indexOf(event.keyCode) !== -1 ||
        // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X (and Cmd on Mac)
        (event.keyCode === 65 && (event.ctrlKey === true || event.metaKey === true)) ||
        (event.keyCode === 67 && (event.ctrlKey === true || event.metaKey === true)) ||
        (event.keyCode === 86 && (event.ctrlKey === true || event.metaKey === true)) ||
        (event.keyCode === 88 && (event.ctrlKey === true || event.metaKey === true)) ||
        // Allow: home, end, left, right
        (event.keyCode >= 35 && event.keyCode <= 39)) {
        return;
      }

      // Ensure that it is a number and stop the keypress
      if ((event.shiftKey || (event.keyCode < 48 || event.keyCode > 57)) &&
        (event.keyCode < 96 || event.keyCode > 105)) {
        event.preventDefault();
      }
    },
    handlePaste(event) {
      event.preventDefault();
      const pastedText = (event.clipboardData || window.clipboardData).getData('text');
      const cleaned = this.cleanDate(pastedText);
      const formatted = this.formatDate(cleaned);

      this.displayValue = formatted;
      const isoDate = this.toISODate(cleaned);
      if (isoDate || cleaned === '') {
        this.$emit('update:modelValue', isoDate);
      }
    },
    handleFocus(event) {
      this.isFocused = true;
      // Auto-select all text for easy overwriting
      event.target.select();
    },
    handleBlur() {
      this.isFocused = false;
      // Re-format on blur to ensure consistency
      const cleaned = this.cleanDate(this.displayValue);
      this.displayValue = this.formatDate(cleaned);
      this.$emit('blur');
    },
    cleanDate(value) {
      // Remove all non-numeric characters
      return (value || '').replace(/\D/g, '');
    },
    formatDate(value) {
      if (!value) return '';

      // If value looks like ISO date (YYYY-MM-DD), convert to MM/DD/YYYY
      if (value.includes('-') && value.length === 10) {
        const [year, month, day] = value.split('-');
        return `${month}/${day}/${year}`;
      }

      const cleaned = this.cleanDate(value);

      // Format: MM/DD/YYYY
      if (cleaned.length === 0) return '';
      if (cleaned.length <= 2) return cleaned;
      if (cleaned.length <= 4) return `${cleaned.slice(0, 2)}/${cleaned.slice(2)}`;
      return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}/${cleaned.slice(4, 8)}`;
    },
    toISODate(cleaned) {
      // Convert MMDDYYYY to YYYY-MM-DD
      if (cleaned.length === 8) {
        const month = cleaned.slice(0, 2);
        const day = cleaned.slice(2, 4);
        const year = cleaned.slice(4, 8);

        // Basic validation
        const monthNum = parseInt(month, 10);
        const dayNum = parseInt(day, 10);
        const yearNum = parseInt(year, 10);

        if (monthNum >= 1 && monthNum <= 12 && dayNum >= 1 && dayNum <= 31 && yearNum >= 1900) {
          return `${year}-${month}-${day}`;
        }
      }
      return '';
    }
  },
  emits: ['update:modelValue', 'blur']
}
</script>

<style scoped>
/* Inherits .form-control styles from global.css */
</style>
